import { StandardEvents } from '@shopify/events';

/**
 * B2B-only variant-option gate for retail/guest shoppers (posture A).
 *
 * Hiding itself is pure server-rendered CSS (see blocks/bbd-b2b-options.liquid) - this
 * element only handles what genuinely requires JS:
 *
 * - Auto-heal: because the gated options are hidden, a guest can strand the selection on
 *   an unavailable/out-of-stock combination they cannot fix (e.g. they change a visible
 *   option while a hidden one stays frozen on a value that has no stock). When that
 *   happens we switch ONLY the hidden options to reach the first available variant that
 *   still matches the guest's visible choices, then let the native picker re-resolve.
 * - Availability correction: a hidden option's currently-checked value still counts as
 *   "selected" for Shopify's own per-value `available` computation, so a VISIBLE option's
 *   values can flip between falsely sold-out and falsely available purely depending on
 *   which hidden value happens to be pinned - not on genuine stock. We recompute each
 *   visible value's true availability (ignoring hidden options entirely) and correct the
 *   data-option-available attribute that assets/base.css keys its sold-out styling off of.
 *   Dropdown-style option rendering carries no data-option-available attribute at all
 *   today, so it isn't corrected here (pre-existing gap, not a regression).
 * - Per-button price correction: variant-main-picker.liquid's price_visible_variant_options
 *   feature computes each button's own price label the same hidden-value-fixed way, so a
 *   button whose only real variant needs a DIFFERENT hidden value never gets a price label
 *   at all. We recompute the matched variant the same hidden-agnostic way and create/fix the
 *   price span, then delegate the actual quantity/tier formatting back to the sibling
 *   assets/bbd-variant-option-prices.js component (via its public `updateAll()`) rather than
 *   duplicating its tier-lookup logic.
 *
 * Auto-heal, availability correction, and price correction all run after Horizon's native
 * ProductSelectEvent resolves (fired by variant-picker.js once its morph has completed),
 * matching the pattern already used by the sibling block assets/bbd-variant-option-prices.js
 * - no MutationObserver needed.
 *
 * A second, pre-emptive path also runs the same heal logic during the CAPTURE phase of the
 * shopper's own `change` event, before variant-picker.js's bubble-phase listener reads the
 * live DOM to build its fetch request. Without this, a shopper changing a visible option
 * while a hidden option is stale would cause variant-picker.js to fetch an invalid
 * combination first (Shopify's own `selected_or_first_available_variant` Liquid fallback then
 * silently renders a *different, wrong* variant's price), and only a second, separate
 * corrective fetch - triggered by the reactive path above - would show the right price a
 * moment later (or never, if that second fetch gets superseded and aborted by a further
 * shopper action). Correcting the hidden option before the first fetch is built means only
 * one, already-correct fetch ever happens - no wrong price, no race.
 */
class BbdB2bOptions extends HTMLElement {
  /** @type {string[]} */
  #names = [];

  /** @type {string} */
  #soldOutLabel = '';

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#names = (this.dataset.hiddenOptions || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);
    if (this.#names.length === 0) return;

    this.#soldOutLabel = this.dataset.soldOutLabel || '';

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    closestSection?.addEventListener('change', this.#interceptChange, { capture: true, signal });

    this.#apply();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * variant-picker.js dispatches this once per option change, with a promise that
   * resolves after its morph has already run - so we react strictly after the DOM the
   * shopper sees has settled, not mid-update.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(() => this.#apply())
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bbd-b2b-options] Product select promise rejected:', error);
        }
      });
  };

  #apply() {
    const picker = this.#picker();
    if (!picker) return;
    this.#autoHeal(picker);
    this.#correctAvailability(picker);
    this.#correctOptionPrices(picker);
  }

  /** @returns {Element | null} */
  #picker() {
    return this.closest('variant-picker') || document.querySelector('variant-picker');
  }

  /**
   * If the current selection isn't a real variant, flip only the hidden (gated) options to
   * reach one that is - preferring an available match, but falling back to an unavailable one
   * rather than leaving the hidden option pointing at a combination that doesn't exist at all
   * (e.g. shopper clicks a height that only exists under a DIFFERENT hidden grade than the one
   * still pinned: without this, the next fetch would request that nonexistent pairing, and
   * Shopify's own fallback resolution lands on some unrelated variant - wrong/stale price,
   * broken per-button prices, all of it). Then dispatch a native change so the variant picker
   * re-resolves (URL, price, inventory, etc.). Reactive fallback for the initial page-load case
   * (the SSR'd default variant can itself be invalid, with no preceding shopper `change` event
   * to pre-empt) - a harmless no-op otherwise, since #interceptChange already corrects the
   * common shopper-driven case before this runs.
   * @param {Element} picker
   */
  #autoHeal(picker) {
    const heal = this.#computeHeal(picker);
    if (!heal) return;
    heal.input.checked = true;
    // One dispatch is enough; the picker reads all :checked inputs on change.
    heal.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Pre-empts the price race entirely for shopper-driven changes: runs during the CAPTURE
   * phase on an ancestor of <variant-picker>, before variant-picker.js's own `change` listener
   * (a plain this.addEventListener('change', ...) on <variant-picker> itself, so it only fires
   * during the BUBBLE phase) reads the live DOM to build its fetch request. Correcting the
   * hidden option's checked state here means the shopper's very first (and only) fetch already
   * targets the healed, valid variant - no dispatch needed, since the original event is still
   * propagating and will reach variant-picker.js's listener right after this handler returns.
   * @param {Event} event
   */
  #interceptChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const picker = this.#picker();
    if (!picker || event.target.closest('variant-picker') !== picker) return;

    // A <select>'s data-option-name lives on its <option> children, not the select itself.
    const changedOption = event.target instanceof HTMLSelectElement ? event.target.selectedOptions[0] : event.target;
    const changedName = (changedOption?.dataset?.optionName || '').trim().toLowerCase();
    if (!changedName || this.#names.includes(changedName)) return; // ignore hidden options' own changes

    const heal = this.#computeHeal(picker);
    if (heal) heal.input.checked = true;
  };

  /**
   * Determines which hidden option's input needs to change, and to what value, to reach the
   * variant the shopper would actually end up on.
   * @param {Element} picker
   * @returns {{ input: HTMLInputElement, value: string } | null} null when the current
   *   combination is already real (don't fight the shopper) or truly nothing matches at all.
   */
  #computeHeal(picker) {
    const variants = this.#variants();
    const order = this.#optionOrder(picker); // positional option names, product order
    const hidden = this.#names;
    const valuesByName = new Map(this.#checkedOptions(picker).map((o) => [o.name.trim().toLowerCase(), o.value]));

    // requireAvailable: false - reaching a REAL variant (even out of stock) always beats
    // leaving the hidden option stuck on a value that pairs with nothing.
    const target = this.#resolveVariant(variants, order, hidden, valuesByName, { requireAvailable: false });
    if (!target) return null; // nothing matches at all

    for (const name of hidden) {
      const idx = order.indexOf(name);
      if (idx === -1) continue;
      const value = target.options[idx];
      const input = /** @type {HTMLInputElement | null} */ (
        picker.querySelector(
          `input[data-option-name="${CSS.escape(this.#displayName(picker, name))}"][value="${CSS.escape(value)}"]`
        )
      );
      if (input && !input.checked) return { input, value };
    }
    return null; // target is already the current combination — nothing to flip
  }

  /**
   * The single source of truth for "which real variant would the shopper actually end up on,"
   * given a set of option values (by lowercased name). Mirrors auto-heal's own resolution
   * exactly, so a per-button price prediction can never disagree with what clicking the
   * button actually produces: if the given values (hidden ones included, as they currently
   * stand) already match a real variant, that's the answer - don't fight the shopper.
   * Otherwise, fall back to the first variant that still honours every VISIBLE value, treating
   * hidden options as free variables only in this fallback step (this is exactly what
   * auto-heal would switch the hidden option to).
   * @param {Array<{ available: boolean, options: string[], price?: number }>} variants
   * @param {string[]} order - positional option names (lowercased), product order
   * @param {string[]} hidden - lowercased hidden option names
   * @param {Map<string, string>} valuesByName - lowercased option name → currently/hypothetically
   *   selected value, for every option (visible and hidden) whose value matters
   * @param {{ requireAvailable?: boolean }} [opts] - requireAvailable (default true) restricts
   *   both the exact match and the fallback to purchasable variants, which is essential for
   *   auto-heal (never switch a shopper onto stock they can't buy). Price display wants the
   *   opposite - an indicative price is still useful even at 0 stock - so price correction
   *   passes `false` here.
   * @returns {object | null} the resolved variant, or null if nothing matches at all
   */
  #resolveVariant(variants, order, hidden, valuesByName, { requireAvailable = true } = {}) {
    if (variants.length === 0 || order.length === 0) return null;

    const has = (name, v, i) => valuesByName.get(name) === undefined || v.options[i] === valuesByName.get(name);
    const matchesAll = (v) => order.every((name, i) => has(name, v, i));
    const matchesVisible = (v) => order.every((name, i) => hidden.includes(name) || has(name, v, i));

    const current = variants.find(matchesAll);
    if (current?.available) return current; // already fine — don't fight the shopper

    const availableFallback = variants.find((v) => v.available && matchesVisible(v));
    if (availableFallback) return availableFallback;

    if (requireAvailable) return null; // nothing rescuable → null

    // Nothing purchasable at all: still show an indicative price rather than none, preferring
    // the exact current combination (out of stock) over a different hidden-value match.
    return current ?? variants.find(matchesVisible) ?? null;
  }

  /**
   * Recomputes true availability for every VISIBLE option's rendered values, ignoring
   * whatever a hidden option currently happens to be pinned to, and corrects
   * data-option-available (which assets/base.css keys its sold-out styling off of)
   * accordingly. This is what stops a visible option's sold-out state from flip-flopping
   * depending on which hidden-option value auto-heal last landed on.
   * @param {Element} picker
   */
  #correctAvailability(picker) {
    const variants = this.#variants();
    if (variants.length === 0) return;

    const order = this.#optionOrder(picker);
    if (order.length === 0) return;

    const hidden = this.#names;
    const checkedByName = new Map(this.#checkedOptions(picker).map((o) => [o.name.trim().toLowerCase(), o.value]));

    for (const container of picker.querySelectorAll('.variant-option[data-option-name]')) {
      const name = (container.dataset.optionName || '').trim().toLowerCase();
      if (!name || hidden.includes(name)) continue; // hidden options are display:none — skip
      const idx = order.indexOf(name);
      if (idx === -1) continue;

      for (const input of container.querySelectorAll('input[type="radio"][data-option-available]')) {
        const candidate = /** @type {HTMLInputElement} */ (input).value;

        // A value absent from this product's own variant map belongs to a combined-listing
        // child product — leave the server's own answer alone.
        if (!variants.some((v) => v.options[idx] === candidate)) continue;

        const trulyAvailable = variants.some((v) => {
          if (!v.available || v.options[idx] !== candidate) return false;
          for (let i = 0; i < order.length; i++) {
            if (i === idx || hidden.includes(order[i])) continue; // hidden options are free variables
            const want = checkedByName.get(order[i]);
            if (want === undefined) continue; // unresolvable → don't constrain
            if (v.options[i] !== want) return false;
          }
          return true;
        });

        this.#setAvailability(/** @type {HTMLInputElement} */ (input), trulyAvailable);
      }
    }
  }

  /**
   * Server-rendered per-button price labels (variant-main-picker.liquid's
   * price_visible_variant_options feature) compute their matched variant by holding every
   * OTHER option fixed to the currently selected variant's values - including a hidden
   * option's stale value. So a button whose only real variant needs a DIFFERENT hidden value
   * than whatever's currently selected never gets a price label rendered at all (or keeps a
   * stale one). For each candidate button we predict the variant the shopper would *actually*
   * end up on if they clicked it - via the exact same #resolveVariant auto-heal itself uses -
   * rather than guessing an "available" alternative independent of what's really selected;
   * that guess can silently disagree with what clicking the button actually produces,
   * flipping the shown price the moment the shopper picks it. Fix up (or create) the span.
   *
   * Unlike auto-heal, this never requires the resolved variant to be `available` - a button
   * whose only real variant is out of stock should still show its price (e.g. 0,75 rather
   * than nothing), it just won't be purchasable until restocked.
   * @param {Element} picker
   */
  #correctOptionPrices(picker) {
    const variants = this.#variants();
    const order = this.#optionOrder(picker);
    const hidden = this.#names;
    const valuesByName = new Map(this.#checkedOptions(picker).map((o) => [o.name.trim().toLowerCase(), o.value]));

    let touched = false;

    for (const container of picker.querySelectorAll('.variant-option[data-option-name]')) {
      const name = (container.dataset.optionName || '').trim().toLowerCase();
      if (!name || hidden.includes(name)) continue; // hidden options are display:none — skip
      // Only correct options Liquid actually opted into showing per-button prices for.
      if (!container.querySelector('[data-key="variant-option-price"]')) continue;

      for (const input of container.querySelectorAll('input[type="radio"]')) {
        const radio = /** @type {HTMLInputElement} */ (input);

        // What clicking this button would actually resolve to, hidden option included: same
        // values, except this one visible position hypothetically set to the candidate.
        const hypothetical = new Map(valuesByName);
        hypothetical.set(name, radio.value);
        const resolved = this.#resolveVariant(variants, order, hidden, hypothetical, { requireAvailable: false });

        if (resolved?.price == null) continue;
        if (this.#setOptionPrice(radio, resolved)) touched = true;
      }
    }

    if (touched) {
      /** @type {{ updateAll?: () => void } | null} */
      const priceComponent = picker.querySelector('bbd-variant-option-prices');
      priceComponent?.updateAll?.();
    }
  }

  /**
   * Ensures the given radio's price span exists with the matched variant's price/tiers,
   * creating it if Liquid never rendered one (matching its exact markup structure).
   * @param {HTMLInputElement} input
   * @param {{ price: number, tiers?: unknown[] }} matched
   * @returns {boolean} true if anything changed
   */
  #setOptionPrice(input, matched) {
    const label = input.closest('.variant-option__button-label');
    if (!label) return false;

    const priceStr = String(matched.price);
    const tiersJson = matched.tiers?.length ? JSON.stringify(matched.tiers) : '';

    let span = /** @type {HTMLElement | null} */ (label.querySelector('[data-key="variant-option-price"]'));
    if (span && span.dataset.variantPrice === priceStr && (span.dataset.tiers || '') === tiersJson) {
      return false; // already correct
    }

    if (!span) {
      span = document.createElement('span');
      span.className = 'variant-option__button-label__price';
      span.dataset.key = 'variant-option-price';
      span.append('– € ');
      const amount = document.createElement('span');
      amount.dataset.key = 'variant-option-price-amount';
      span.append(amount);
      const textEl = label.querySelector('[data-key="variant-option-text"]');
      textEl ? textEl.after(span) : label.append(span);
    }

    span.dataset.variantPrice = priceStr;
    if (tiersJson) span.dataset.tiers = tiersJson;
    else delete span.dataset.tiers;

    return true;
  }

  /**
   * Mirrors what Liquid would have rendered for this availability value - keeps
   * aria-disabled, the aria-label's sold-out suffix, and the selected-state pill in sync
   * with the (possibly corrected) data-option-available attribute.
   * @param {HTMLInputElement} input
   * @param {boolean} available
   */
  #setAvailability(input, available) {
    const next = available ? 'true' : 'false';
    if (input.dataset.optionAvailable === next) return; // already correct, nothing to do

    input.dataset.optionAvailable = next;

    if (available) input.removeAttribute('aria-disabled');
    else input.setAttribute('aria-disabled', 'true');

    if (this.#soldOutLabel) {
      const suffix = ` - ${this.#soldOutLabel}`;
      let base = input.getAttribute('aria-label') || '';
      if (base.endsWith(suffix)) base = base.slice(0, -suffix.length);
      input.setAttribute('aria-label', available ? base : base + suffix);
    }

    const label = input.closest('.variant-option__button-label');
    if (label && !label.classList.contains('variant-option__button-label--has-swatch')) {
      const pill = label.querySelector('.variant-option__button-label__pill');
      if (available && !pill) {
        const span = document.createElement('span');
        span.className = 'variant-option__button-label__pill';
        span.dataset.key = 'variant-option-pill';
        input.after(span);
      } else if (!available && pill) {
        pill.remove();
      }
    }
  }

  /** Parsed minimal variant map emitted by blocks/bbd-b2b-options.liquid. */
  #variants() {
    try {
      const el = this.querySelector('script[data-all-variants]') || document.querySelector('script[data-all-variants]');
      const data = el ? JSON.parse(el.textContent || '[]') : [];
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  /** Positional list of option names (lowercased), in product order. */
  #optionOrder(picker) {
    const names = [];
    for (const el of picker.querySelectorAll('[data-option-name]')) {
      const n = (el.dataset.optionName || '').trim().toLowerCase();
      if (n && !names.includes(n)) names.push(n);
    }
    return names;
  }

  /** Original-cased option name as it appears in the DOM (for attribute selectors). */
  #displayName(picker, lowerName) {
    const el = Array.from(picker.querySelectorAll('[data-option-name]')).find(
      (el) => (el.dataset.optionName || '').trim().toLowerCase() === lowerName
    );
    return el ? el.dataset.optionName : lowerName;
  }

  /** Currently selected options (radios + selects). */
  #checkedOptions(picker) {
    const out = [];
    for (const input of picker.querySelectorAll('fieldset input:checked')) {
      if (input.dataset?.optionName) out.push({ name: input.dataset.optionName, value: input.value });
    }
    for (const select of picker.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) out.push({ name: selected.dataset.optionName, value: selected.value });
    }
    return out;
  }
}

if (!customElements.get('bbd-b2b-options')) {
  customElements.define('bbd-b2b-options', BbdB2bOptions);
}
